import { Component, ComponentInterface, Element, Event, EventEmitter, h, Method, Prop, State, Watch } from "@stencil/core";

@Component({
    tag: "uic-virtual-scroll",
    styleUrl: "virtual-scroll.scss"
})
export class VirtualScroll implements ComponentInterface {
    @Element() host: HTMLUicVirtualScrollElement;

    @State() private currentSlice = [];

    private scrollTop = 0;
    private height = 0;

    private firstIndex = 0;
    private lastIndex = 0;
    private totalHeight = 0;

    private scrollPanel: HTMLUicScrollpanelElement;
    private scrollingElement: HTMLElement;
    private scrollingHandler: (event: Event) => void;

    private hasFiredScrollToEnd = false;
    private latestFetch;

    /**
     * The height of an item.
     */
    @Prop() readonly itemHeight: number = 10;

    /**
     * Amount of items to render in addition to visible items of the viewport.
     * Keep this number as low as possible to enhance performance.
     */
    @Prop() readonly buffer: number = 2;

    /**
     * The amount of items to render or an array with items to render.
     * The former option requires setting the `getItems` function.
     */
    // eslint-disable-next-line
    @Prop() readonly items: number | any[] = [];

    /**
     * A function to retrieve items in a given range.
     * Called every time the viewport is updated, e.g. by scrolling.
     */
    // eslint-disable-next-line
    @Prop() readonly getItems: (
        first: number,
        last: number
        // eslint-disable-next-line
    ) => any[] | Promise<any[]>;

    /**
     * A function that renders a given item of the data source.
     */
    // eslint-disable-next-line
    @Prop() readonly renderItemFn: (item: any) => any;

    /**
     * Emitted when user scrolled near the end of the list.
     * The actual activation point depends on the size of the buffer.
     */
    @Event() uicScrollToEnd: EventEmitter;

    disconnectedCallback() {
        // eslint-disable-next-line
        if (this.scrollingElement) {
            this.scrollingElement.removeEventListener("scroll", this.scrollingHandler);
        }
    }

    @Watch("items")
    handleItemsChange() {
        this.hasFiredScrollToEnd = false;

        this.updateViewport();
    }

    @Watch("getItems")
    handleGetItemsChange() {
        this.updateViewport();
    }

    /**
     * Checks height of scrolling element and re-renders component on change.
     */
    @Method()
    async checkViewportSize() {
        const height = this.getItemsContainerHeight();

        if (height !== this.height) {
            this.height = height;
            this.updateViewport();
        }
    }

    private getTotalItems(): number {
        return Array.isArray(this.items) ? this.items.length : this.items;
    }

    private getItemsContainerHeight(): number {
        // eslint-disable-next-line
        return this.scrollingElement ? this.scrollingElement.clientHeight : 0;
    }

    // eslint-disable-next-line
    private handleScroll = (event: any) => {
        this.scrollTop = event.target.scrollTop;
        this.height = this.getItemsContainerHeight();

        this.checkScrollToEnd();
        this.updateViewport();
    };

    private checkScrollToEnd() {
        if (!this.hasFiredScrollToEnd && this.scrollTop + this.height >= this.totalHeight - this.buffer * this.itemHeight) {
            this.hasFiredScrollToEnd = true;
            this.uicScrollToEnd.emit();
        }
    }

    /**
     * Handles event of discovering new scroll panel.
     *
     * @param scrollPanel scroll panel to be discovered
     */
    private async discoverScrollPanel(scrollPanel: Element) {
        if (this.scrollPanel !== scrollPanel) {
            this.disconnectedCallback();

            this.scrollPanel = scrollPanel as HTMLUicScrollpanelElement;
            this.scrollingElement = await this.scrollPanel.getScrollElement();

            this.scrollingHandler = (event) => this.handleScroll(event);
            this.scrollingElement.addEventListener("scroll", this.scrollingHandler);

            this.scrollTop = 0;
            this.height = this.getItemsContainerHeight();

            this.updateViewport();
        }
    }

    /**
     * Updates meta information on current slice of items to be visible in
     * virtual scroller and retrieves related slice from provided data or by
     * invoking provided callback.
     *
     * @private
     */
    private updateViewport() {
        const totalItems = this.getTotalItems();
        const { itemHeight, buffer } = this;

        this.totalHeight = totalItems * itemHeight;

        let first = Math.max(0, Math.floor(this.scrollTop / itemHeight));
        let last = Math.min(first + Math.ceil(this.height / itemHeight) + 1);

        first = Math.max(0, first - buffer);
        last = Math.min(totalItems, last + buffer);

        if (Array.isArray(this.items)) {
            this.firstIndex = first;
            this.lastIndex = last;

            this.currentSlice = this.items.slice(first, last);
        } else if (first !== this.firstIndex || last !== this.lastIndex) {
            if (typeof this.getItems === "function") {
                this.firstIndex = first;
                this.lastIndex = last;

                const fetch = (this.latestFetch = this.getItems(first, last));

                if (fetch instanceof Promise) {
                    fetch.then((slice) => {
                        if (this.latestFetch === fetch) {
                            this.currentSlice = slice;
                        }
                    });
                } else if (Array.isArray(fetch)) {
                    this.currentSlice = fetch;
                }
            } else {
                // eslint-disable-next-line
                console.error("virtual-scroll requires callback getItems() for retrieving listed items");
            }
        }
    }

    render() {
        const totalHeight = `${this.totalHeight}px`;

        let index = this.firstIndex;

        const renderer = this.renderItemFn || (() => <div class='missing-renderer'></div>);

        return (
            <uic-scrollpanel class='uic-virtual-scroll__scroller' ref={(el) => this.discoverScrollPanel(el)}>
                <div style={{ height: totalHeight }}>
                    {this.currentSlice.map((item) => (
                        <div
                            class='uic-virtual-scroll__item'
                            style={{
                                top: `${index++ * this.itemHeight}px`
                            }}
                        >
                            {renderer.call(this, item)}
                        </div>
                    ))}
                </div>
            </uic-scrollpanel>
        );
    }
}
